import {
	clearSessionData,
	getAuth,
	getBusinessData,
	saveAuth,
	saveBusinessData,
} from '@workinance/shared/database';
import { type IAuthenticatedUser } from '@workinance/shared/interfaces/auth.interface';
import { type IBusiness } from '@workinance/shared/interfaces/business.interface';

export interface IAuthData {
	token: string;
	/** Renews `token` when it expires. Saved with it, in the same record. */
	refreshToken?: string;
	user: IAuthenticatedUser;
	businesses?: IBusiness[];
	activeBusinessId?: string | null;
}

type IAuthRecord = Pick<IAuthData, 'token' | 'refreshToken' | 'user'>;
type IBusinessRecord = Omit<IAuthData, 'token' | 'refreshToken' | 'user'>;

/** Saves the session as two records: who is signed in, and which business it works with. */
export async function saveAuthData<TAuthData extends IAuthData = IAuthData>(
	authData: TAuthData,
): Promise<void> {
	const { token, refreshToken, user, ...businessData } = authData;
	// Most saves are the same session with something else changed and do not carry the refresh token,
	// so the one already stored is kept rather than overwritten.
	const keptRefreshToken =
		refreshToken ?? (await getAuth<IAuthRecord>())?.refreshToken;
	await Promise.all([
		saveAuth<IAuthRecord>({ token, refreshToken: keptRefreshToken, user }),
		saveBusinessData<IBusinessRecord>(businessData),
	]);
}

/** Reads the two records back as one session. Null when nobody is signed in. */
export async function getAuthData<
	TAuthData extends IAuthData = IAuthData,
>(): Promise<TAuthData | null> {
	const [auth, businessData] = await Promise.all([
		getAuth<IAuthRecord>(),
		getBusinessData<IBusinessRecord>(),
	]);
	return auth ? ({ ...businessData, ...auth } as TAuthData) : null;
}

/** Clears the session (auth and business). What the device remembers about the last sign in stays. */
export async function clearAuthData(): Promise<void> {
	await clearSessionData();
}
